import { logger } from '@utils/safeLogger'
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from './axiosConfig'
import {
  IChatConversation,
  IChatMessage,
  IChatParticipant,
} from '@dataTypes/chat.type'

import { getEnv } from '@utils/env'
import { toApiError } from './errors/api-error'

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type AllConversationResponseData = {
  conversations?: IChatConversation[]
  chat?: IChatMessage
}

export type ConversationResponseData = {
  conversation?: IChatConversation
  participants?: IChatParticipant[]
}

export type ChatResponseData = {
  chat?: IChatMessage
}

export type LinkPreviewResponseData = {
  linkPreview: { url: string; image: string }
}

type GenericResponseData = Record<string, unknown>

type GetAllChatsResponse = ApiResponse<AllConversationResponseData>
type AddConversationResponse = ApiResponse<ConversationResponseData>
type AddChatResponse = ApiResponse<AllConversationResponseData>
type UpdateChatResponse = ApiResponse<ChatResponseData>
type UpdateConversationResponse = ApiResponse<AllConversationResponseData>
type BulkUpdateConversationResponse = ApiResponse<GenericResponseData>
type BulkUpdateChatResponse = ApiResponse<GenericResponseData>
type DeleteConversationResponse = ApiResponse<GenericResponseData>
type BulkDeleteConversationsResponse = ApiResponse<{ deleted_ids?: string[] }>
type DeleteChatParticipantResponse = ApiResponse<GenericResponseData>
type MarkConversationAsReadResponse = ApiResponse<{
  participant?: IChatParticipant
}>
type LinkPreviewResponse = ApiResponse<LinkPreviewResponseData>

const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_CHAT')
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CHAT')

const headers = {
  'Content-Type': 'application/json',
}

// Function to get all chat conversations
export const getAllChatConversation = async (
  chatType?: 'Internal' | 'External',
  user_id?: string,
): Promise<GetAllChatsResponse> => {
  try {
    let params: Record<string, string> | undefined = chatType
      ? { chat_type: chatType }
      : undefined

    if (user_id) {
      if (params) params.user_id = user_id
      else params = { user_id }
    }

    const response = await axios.get<GetAllChatsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/conversation/all`,
      {
        headers,
        params,
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const addChat = async (data: IChatMessage): Promise<AddChatResponse> => {
  try {
    const response = await axios.post<AddChatResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in addChat:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const addConversation = async (
  conversation_data: IChatConversation,
  members_data: IChatParticipant[],
): Promise<AddConversationResponse> => {
  try {
    const response = await axios.post<AddConversationResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/conversation`,
      { conversation_data, members_data },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in addConversation:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateChatById = async (
  chat_message_id: string,
  data: Partial<IChatMessage>,
): Promise<UpdateChatResponse> => {
  try {
    const response = await axios.put<UpdateChatResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat`,
      { chat_message_id, data },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateChatById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateMultipleChatMessages = async (
  chat_message_ids: string[],
  data: Partial<IChatMessage>,
): Promise<BulkUpdateChatResponse> => {
  try {
    const response = await axios.put<BulkUpdateChatResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/bulk`,
      { chat_message_ids, data },
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateMultipleChatMessages:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateConversationById = async (
  chat_conversation_id: string,
  data: Partial<IChatConversation>,
): Promise<UpdateConversationResponse> => {
  try {
    const response = await axios.put<UpdateConversationResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/conversation`,
      { chat_conversation_id, data },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateConversationById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateMultipleConversation = async (
  chat_conversation_ids: string[],
  data: Partial<IChatConversation>,
): Promise<BulkUpdateConversationResponse> => {
  try {
    const response = await axios.put<BulkUpdateConversationResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/conversation/bulk`,
      { chat_conversation_ids, data },
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateMultipleConversation:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const sendSms = async (
  smsMessage: IChatMessage,
  sid: string,
  token: string,
  meta?: { clinic_id?: string; clinic_number_id?: string },
): Promise<AddChatResponse> => {
  const dbName = localStorage.getItem('db_name')
  try {
    const response = await axios.post<AddChatResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/chat/sms`,
      { data: smsMessage, sid, token, ...meta },
      {
        headers: dbName ? { ...headers, 'x-db-name': dbName } : headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in sendSms:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getBulkPresignedUrl = async (
  conversations: IChatConversation[],
): Promise<GetAllChatsResponse> => {
  try {
    const response = await axios.put<GetAllChatsResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/chat/bulk-presigned-url`,
      { conversations },
      {
        headers: {
          ...headers,
        },
      },
    )
    return response.data
  } catch (e: any) {
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteConversationById = async (
  conversationId: string,
): Promise<DeleteConversationResponse> => {
  try {
    const response = await axios.delete<DeleteConversationResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/conversation/${conversationId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteConversationById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const bulkDeleteConversationsById = async (
  chatConversationIds: string[],
  userId?: string,
): Promise<BulkDeleteConversationsResponse> => {
  try {
    if (!chatConversationIds?.length) {
      return { statusCode: 200, message: 'OK', data: { deleted_ids: [] } }
    }
    const response = await axios.delete<BulkDeleteConversationsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/conversation/bulk`,
      {
        headers,
        data: {
          chat_conversation_ids: chatConversationIds,
          ...(userId && { user_id: userId }),
        },
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in bulkDeleteConversationsById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteChatParticipantById = async (
  chatParticipantId: string,
): Promise<DeleteChatParticipantResponse> => {
  try {
    const response = await axios.delete<DeleteChatParticipantResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/participant/${chatParticipantId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteChatParticipantById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const markConversationAsRead = async (
  chat_conversation_id: string,
  user_id: string,
): Promise<MarkConversationAsReadResponse> => {
  try {
    const response = await axios.put<MarkConversationAsReadResponse>(
      `${BUSINESS_LAYER_API}/api/v1/chat/participant/read`,
      { chat_conversation_id, user_id },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in markConversationAsRead:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const bulkUpdateChatParticipants = async (
  chatParticipantIds: string[],
  data: Partial<IChatParticipant>,
): Promise<ApiResponse<GenericResponseData>> => {
  try {
    const response = await axios.put<ApiResponse<GenericResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/chat/participant/bulk`,
      { chat_participant_ids: chatParticipantIds, data },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in bulkUpdateChatParticipants:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getLinkPreview = async (
  url: string,
): Promise<LinkPreviewResponse> => {
  try {
    const response = await axios.get<LinkPreviewResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/chat/link-preview`,
      {
        headers,
        params: { url },
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getLinkPreview:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
